import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';

import { environment } from '../../../../environments/environment.development';
import { Branch } from '../../../auth/models/user.model';
import { SessionService } from '../../../auth/services/session/session.service';
import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { CustomerReportComponent } from '../../components/customer-report/customer-report.component';
import { DocumentsService } from '../../services/documents/documents.service';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [
    LayoutComponent,
    CalendarModule,
    ReactiveFormsModule,
    DropdownModule,
    CustomerReportComponent,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent implements OnInit {
  isExportingPdf = false;
  pdfWindow: Window | null = null;
  private _sessionService = inject(SessionService);

  public reportsForm: FormGroup;
  public clients: any[] = [];
  public initialDate: any = undefined;
  public finalDate: any = undefined;

  constructor(
    private _formBuilder: FormBuilder,
    private _documentsService: DocumentsService,
    private _messageService: MessageService,
  ) {
    this.reportsForm = this._formBuilder.group({
      initial_date: [null, Validators.required],
      final_date: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.clients = this._sessionService.Customer.branch.length > 0
      ? this._sessionService.Customer.branch.map((client: Branch) => ({
        id: client[0],
        name: client[1],
        alias: client[2],
      }))
      : [
        {
          id: this._sessionService.Customer.id,
          name: this._sessionService.Customer.name,
          alias: this._sessionService.Customer.contact_name,
        }
      ];
  }

  get customerId(): string {
    return this._sessionService.Customer.id;
  }

  exportPdfBackend(): void {
    this.reportsForm.markAllAsTouched();

    if (!this.canExportPdf) return;

    this.isExportingPdf = true;

    const customerId = this._sessionService.Customer.id;

    const fromTime = this.reportsForm.value.initial_date
      .toISOString()
      .split('T')[0];

    const toTime = this.reportsForm.value.final_date
      .toISOString()
      .split('T')[0];

    const url = `/customers/${customerId}/pdf_trends/`;

    this.pdfWindow = window.open('', '_blank');

    if (this.pdfWindow) {
      this.pdfWindow.document.write(`
      <html>
        <head>
          <title>Generando PDF...</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              background: #f4f6f8;
            }
            .loader {
              text-align: center;
            }
            .spinner {
              width: 40px;
              height: 40px;
              border: 4px solid #ccc;
              border-top-color: #d32f2f;
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 12px;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class='loader'>
            <div class='spinner'></div>
            <div>Generando PDF…</div>
          </div>
        </body>
      </html>
    `);
    }

    const payload = {
      from_time: fromTime,
      to_time: toTime,
    };

    this._documentsService.exportPdfBackend(payload, url).subscribe({
      next: (res) => {
        this.isExportingPdf = false;
        const reportUrl = `${environment.documentStorageUrl}${res.data.replace(/^\//, '')}`;

        this.pdfWindow?.location.replace(reportUrl);
      },
      error: () => {
        this.isExportingPdf = false;
        this.pdfWindow?.close();

        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Hubo un error al iniciar sesión. Comprueba tus credenciales e intenta de nuevo.'
        });
      },
    });
  }

  get canExportPdf(): boolean {
    if (this.isExportingPdf) return false;
    if (this.reportsForm.invalid) return false;

    return !this.reportsForm.hasError('dateRangeInvalid');
  }

  onSubmit(event: Event): void {
    event.preventDefault();

    if (this.reportsForm.valid) {
      const initialDate = this.reportsForm.get('initial_date')?.value;
      const formattedInitialDate = initialDate ? initialDate.toISOString().split('T')[0] : null;

      const finalDate = this.reportsForm.get('final_date')?.value;
      const formattedFinalDate = finalDate ? finalDate.toISOString().split('T')[0] : null;

      this.initialDate = formattedInitialDate;
      this.finalDate = formattedFinalDate;
    } else {
      this.reportsForm.markAllAsTouched();
    }
  }
}
